import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Alert,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QuizIcon from '@mui/icons-material/Quiz';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTraining } from '../../../context/TrainingContext';
import axios from 'axios';

const TrainingAssessment = () => {

  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { modules, getTrainingModules } = useTraining();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExamEnded, setIsExamEnded] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!modules || !modules.trainings) {
      getTrainingModules();
    } else {
      const current = modules.trainings.find(t => String(t.id) === String(moduleId));
      if (current && current.assigned_questions) {
        const initialAnswers = {};
        current.assigned_questions.forEach(q => {
          if (q.selected_option) {
            initialAnswers[q.id] = q.selected_option;
          }
        });
        setSelectedAnswers(prev => ({...initialAnswers, ...prev}));
      }
    }
  }, [modules, getTrainingModules, moduleId]);

  const currentTraining = modules?.trainings?.find(
    (t) => String(t.id) === String(moduleId)
  );

  const startAt = currentTraining?.exam_startat || modules?.exam_startat || currentTraining?.exam_start_at || modules?.exam_start_at;

  useEffect(() => {
    if (!startAt) return;

    const startTime = new Date(startAt).getTime();
    if (isNaN(startTime)) return;

    const endTime = startTime + 60 * 60 * 1000;

    const updateTimer = () => {
      const now = Date.now();
      const remainingSeconds = Math.floor((endTime - now) / 1000);

      if (remainingSeconds <= 0) {
        setTimeLeft(0);
        setIsExamEnded(true);
      } else {
        setTimeLeft(remainingSeconds);
        setIsExamEnded(false);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [startAt]);

  const formatTimer = (seconds) => {
    if (seconds === null || isNaN(seconds)) return '--:--';
    if (seconds <= 0) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (!modules || !modules.trainings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4 }}>
        <CircularProgress size={28} sx={{ color: '#1a237e' }} />
        <Typography variant="body1" sx={{ color: '#1a237e', fontWeight: 500 }}>
          Loading assessment...
        </Typography>
      </Box>
    );
  }

  if (!currentTraining) {
    return (
      <Box mt={4}>
        <Alert severity="error">Training module not found.</Alert>
      </Box>
    );
  }

  const questions = currentTraining.assigned_questions || [];

  if (questions.length === 0) {
    return (
      <Box mt={4}>
        <Alert severity="warning">No assessment questions available for this module.</Alert>
      </Box>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  const handleOptionSelect = async (questionId, optionId) => {
    if (isExamEnded) return;

    // Optimistic UI update
    setSelectedAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: optionId
    }));
    
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}api/v1/posp-answer/`,
        {
          assignment_id: questionId,
          option_id: optionId
        }
      );
      // Immediately fetch updated modules to sync state
      await getTrainingModules();
    } catch (error) {
      console.error("Error Submitting Answer:", error);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {   
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}api/v1/complete-posp-exam/`,
        {
          training_id: moduleId,
        }
      );
      if(response?.data?.success){
        setIsExamEnded(true)
        navigate('/app/training');
      }
      else{
        setAlert({ type: 'error', message: response?.data?.message || "Error Submitting Exam!" });
      }
    }catch(error){
      console.error("Error Submitting Exam",error);
      setAlert({ type: 'error', message: "Network Error" });
    }
  };

  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Grid container spacing={1}>
        {alert && (
          <Alert
            severity={alert.type}
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setAlert(null)}
          >
            {alert.message}
          </Alert>
        )}

        {/* Left Pane - Question Area */}
        <Grid size={{xs:12 , md:9}}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)' }}>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QuizIcon sx={{ color: '#1a237e' }} />
                <Typography variant="h6" fontWeight={700} color="#1a237e">
                  Question {currentQuestionIndex + 1}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Timer display on right top */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: isExamEnded ? '#fee2e2' : '#e0f2fe',
                    color: isExamEnded ? '#dc2626' : '#0369a1',
                    px: 2,
                    py: 0.75,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    border: '1px solid',
                    borderColor: isExamEnded ? '#fca5a5' : '#7dd3fc',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 20 }} />
                  <Typography variant="body1" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                    {formatTimer(timeLeft)}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: '#f3e8ff', color: '#6b21a8', px: 2, py: 0.75, borderRadius: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                  Question {currentQuestionIndex + 1} of {questions.length}
                </Box>
              </Box>
            </Box>

            {isExamEnded && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                The exam has ended.
              </Alert>
            )}

            {/* Question Text */}
            <Box sx={{ mb: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  position: "relative",
                  p: 2.5,
                  pt: 3,
                  borderRadius: 2,
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    top: -10,
                    left: 16,
                    px: 0.75,
                    bgcolor: "background.paper",
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Question Text
                </Typography>

                <Typography variant="body1" color="text.primary">
                  {currentQuestion.question_text}
                </Typography>
              </Paper>
            </Box>

            {/* Options */}
            <Box sx={{ position: 'relative' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, ml: 1 }}>
                Options
              </Typography>
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup
                  value={selectedAnswers[currentQuestion.id] || ''}
                  onChange={(e) => handleOptionSelect(currentQuestion.id, parseInt(e.target.value))}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {currentQuestion.options?.map((option, index) => {
                      const isSelected = selectedAnswers[currentQuestion.id] === option.option_id;
                      return (
                        <Box
                          key={option.id}
                          onClick={() => handleOptionSelect(currentQuestion.id, option.option_id)}
                          sx={{
                            border: '1px solid',
                            borderColor: isSelected ? '#4ade80' : 'rgba(0,0,0,0.1)',
                            bgcolor: isSelected ? '#f0fdf4' : 'transparent',
                            borderRadius: 2,
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: isSelected ? '#4ade80' : 'rgba(0,0,0,0.2)'
                            }
                          }}
                        >
                          <FormControlLabel
                            value={option.option_id}
                            control={<Radio sx={{ color: isSelected ? '#4ade80' : 'default', '&.Mui-checked': { color: '#4ade80' } }} />}
                            label=""
                            sx={{ m: 0, mr: 1 }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ color: isSelected ? '#16a34a' : 'text.secondary', display: 'block', mb: 0.5 }}>
                              Option {index + 1}
                            </Typography>
                            <Typography variant="body2" sx={{ color: isSelected ? '#15803d' : 'text.primary' }}>
                              {option.option_text}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </RadioGroup>
              </FormControl>
            </Box>

          </Paper>

          {/* Navigation Pane */}
          <Paper elevation={0} sx={{ mt: 1, p: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handlePrevious}
              disabled={isFirstQuestion}
              sx={{ color: 'text.secondary', borderColor: 'rgba(0,0,0,0.2)', textTransform: 'none' }}
            >
              Previous
            </Button>

            {isLastQuestion ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{ bgcolor: '#312e81', '&:hover': { bgcolor: '#1e1b4b' }, textTransform: 'none' }}
              >
                Submit Assessment
              </Button>
            ) : (
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                onClick={handleNext}
                sx={{ color: '#312e81', borderColor: '#312e81', textTransform: 'none' }}
              >
                Next
              </Button>
            )}
          </Paper>
        </Grid>

        {/* Right Pane - Question List */}
        <Grid size={{xs: 12, md: 3}}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)' }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 2 }}>
              Question List
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {questions.map((q, idx) => {
                const isActive = idx === currentQuestionIndex;
                const isAnswered = !!selectedAnswers[q.id];

                return (
                  <Box
                    key={q.id}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 600 : 400,
                      bgcolor: isActive ? '#312e81' : 'transparent',
                      color: isActive ? '#fff' : (isAnswered ? '#16a34a' : 'text.primary'),
                      border: '1px solid',
                      borderColor: isActive ? '#312e81' : (isAnswered ? '#4ade80' : 'rgba(0,0,0,0.2)'),
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: isActive ? '#312e81' : 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    {idx + 1}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

      </Grid>

      <Dialog
        open={isExamEnded}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            navigate('/app/training');
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>
          Exam Ended
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The exam has ended. Your time is up. Please click the button below to return to your training modules.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="error" 
            sx={{ textTransform: 'none', px: 4, py: 1, borderRadius: 2, fontWeight: 700 }}
          >
            End Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingAssessment;
